/**
 * Canonical domain vocabulary.
 *
 * Every status, kind and category in the system is defined exactly once, here.
 * The database enums, the API contracts and the UI all derive from these
 * constants, so a value can never drift between layers.
 *
 * Traces: CONF-004 (lifecycle), STAT-002 (guarded transitions), DATA-003.
 */

import { z } from 'zod';

/** Helper: turn a readonly tuple into a Zod enum plus its inferred union type. */
const enumOf = <const T extends readonly [string, ...string[]]>(values: T) =>
  z.enum(values);

// ---------------------------------------------------------------------------
// Organisation
// ---------------------------------------------------------------------------

export const OFFICE_STATUSES = ['ACTIVE', 'DISABLED', 'ARCHIVED'] as const;
export const officeStatusSchema = enumOf(OFFICE_STATUSES);
export type OfficeStatus = z.infer<typeof officeStatusSchema>;

export const OFFICE_TYPES = ['MAIN', 'DISTRICT', 'EXTENSION', 'OTHER'] as const;
export const officeTypeSchema = enumOf(OFFICE_TYPES);
export type OfficeType = z.infer<typeof officeTypeSchema>;

export const USER_STATUSES = ['ACTIVE', 'DISABLED', 'ARCHIVED'] as const;
export const userStatusSchema = enumOf(USER_STATUSES);
export type UserStatus = z.infer<typeof userStatusSchema>;

/**
 * ADR-005: a user belongs to exactly one office. Reversing this changes every
 * authorization predicate in the system.
 */
export const ROLE_KEYS = ['ADMIN', 'OFFICE_MANAGER', 'USER', 'VIEWER'] as const;
export const roleKeySchema = enumOf(ROLE_KEYS);
export type RoleKey = z.infer<typeof roleKeySchema>;

// ---------------------------------------------------------------------------
// Confirmation lifecycle
// ---------------------------------------------------------------------------

/**
 * Spec §1 defines DRAFT → SENT → RECEIVED → READ → REPLIED → RESOLVED → CLOSED.
 * CANCELLED is proposal G-01 and is present in the type but gated behind a
 * feature flag until approved — see docs/04-DECISION-LOG.md open items.
 */
export const CONFIRMATION_STATUSES = [
  'DRAFT',
  'SENT',
  'RECEIVED',
  'READ',
  'REPLIED',
  'RESOLVED',
  'CLOSED',
  'CANCELLED',
] as const;
export const confirmationStatusSchema = enumOf(CONFIRMATION_STATUSES);
export type ConfirmationStatus = z.infer<typeof confirmationStatusSchema>;

/**
 * Human-readable status labels.
 *
 * Defined here rather than in the UI so every surface — screens, exports,
 * printed reports — uses the same words for the same state.
 *
 * These describe *sending* between offices, not physical delivery. An earlier
 * revision labelled RECEIVED as "Delivered to office"; that was wrong — nothing
 * is delivered here, a confirmation and its attachments are sent, received by
 * the recipient office's inbox, then opened by a person.
 */
export const CONFIRMATION_STATUS_LABELS: Record<ConfirmationStatus, string> = {
  DRAFT: 'Draft',
  SENT: 'Sent',
  RECEIVED: 'Received by office',
  READ: 'Read',
  REPLIED: 'Replied',
  RESOLVED: 'Resolved',
  CLOSED: 'Closed',
  CANCELLED: 'Cancelled',
};

/** Compact form, for table cells and badges where space is tight. */
export const CONFIRMATION_STATUS_SHORT_LABELS: Record<ConfirmationStatus, string> = {
  ...CONFIRMATION_STATUS_LABELS,
  RECEIVED: 'Received',
};

/** Statuses that accept no further messages without an explicit reopen. */
export const TERMINAL_STATUSES = ['CLOSED', 'CANCELLED'] as const satisfies readonly ConfirmationStatus[];

/** Statuses an overdue confirmation can be in (G-10). */
export const OVERDUE_ELIGIBLE_STATUSES = [
  'SENT',
  'RECEIVED',
  'READ',
] as const satisfies readonly ConfirmationStatus[];

/**
 * Spec §4 `Priority` and §14's SLA tiers are the same field (G-08).
 * The mapping to hours is configuration, not code — see sla_rules.
 */
export const PRIORITIES = ['NORMAL', 'PRIORITY', 'URGENT'] as const;
export const prioritySchema = enumOf(PRIORITIES);
export type Priority = z.infer<typeof prioritySchema>;

/**
 * Which side owes the next message. Drives the "Awaiting Reply" KPI
 * (DASH-002, MSG-007) without a scan over the message table.
 */
export const AWAITING_PARTIES = ['ORIGIN', 'RECIPIENT', 'NONE'] as const;
export const awaitingPartySchema = enumOf(AWAITING_PARTIES);
export type AwaitingParty = z.infer<typeof awaitingPartySchema>;

export const RECIPIENT_KINDS = ['PRIMARY', 'COPY'] as const;
export const recipientKindSchema = enumOf(RECIPIENT_KINDS);
export type RecipientKind = z.infer<typeof recipientKindSchema>;

export const PARTICIPANT_ROLES = [
  'ORIGINATOR',
  'RECIPIENT',
  'ASSIGNEE',
  'OBSERVER',
] as const;
export const participantRoleSchema = enumOf(PARTICIPANT_ROLES);
export type ParticipantRole = z.infer<typeof participantRoleSchema>;

// ---------------------------------------------------------------------------
// Messages
// ---------------------------------------------------------------------------

/**
 * INTERNAL_NOTE is proposal G-05 — an office annotating a confirmation without
 * the counterpart office seeing it. Internal notes never set `replied_at` and
 * are excluded from reply metrics.
 */
export const MESSAGE_TYPES = [
  'INITIAL',
  'REPLY',
  'FOLLOW_UP',
  'INTERNAL_NOTE',
  'SYSTEM',
] as const;
export const messageTypeSchema = enumOf(MESSAGE_TYPES);
export type MessageType = z.infer<typeof messageTypeSchema>;

export const MESSAGE_VISIBILITIES = [
  'ALL_PARTIES',
  'ORIGIN_OFFICE_ONLY',
  'RECIPIENT_OFFICE_ONLY',
] as const;
export const messageVisibilitySchema = enumOf(MESSAGE_VISIBILITIES);
export type MessageVisibility = z.infer<typeof messageVisibilitySchema>;

/** Message types that advance the lifecycle to REPLIED. */
export const REPLY_MESSAGE_TYPES = [
  'REPLY',
  'FOLLOW_UP',
] as const satisfies readonly MessageType[];

// ---------------------------------------------------------------------------
// Attachments
// ---------------------------------------------------------------------------

/**
 * ADR-016: uploads go direct to R2, so validation is deferred to a queue
 * consumer. `scan_status` is the gate that keeps unvalidated files
 * undownloadable by anyone but their uploader (I.3, R-05).
 */
export const SCAN_STATUSES = [
  'PENDING',
  'CLEAN',
  'INFECTED',
  'FAILED',
  'SKIPPED',
] as const;
export const scanStatusSchema = enumOf(SCAN_STATUSES);
export type ScanStatus = z.infer<typeof scanStatusSchema>;

/** Only these statuses permit a download to any authorized participant. */
export const DOWNLOADABLE_SCAN_STATUSES = [
  'CLEAN',
  'SKIPPED',
] as const satisfies readonly ScanStatus[];

export const ATTACHMENT_ACCESS_ACTIONS = ['DOWNLOAD', 'PREVIEW'] as const;
export const attachmentAccessActionSchema = enumOf(ATTACHMENT_ACCESS_ACTIONS);
export type AttachmentAccessAction = z.infer<typeof attachmentAccessActionSchema>;

// ---------------------------------------------------------------------------
// SLA
// ---------------------------------------------------------------------------

export const SLA_STATES = ['ON_TRACK', 'AT_RISK', 'BREACHED', 'MET'] as const;
export const slaStateSchema = enumOf(SLA_STATES);
export type SlaState = z.infer<typeof slaStateSchema>;

/** States the SLA sweep must still evaluate — drives the partial index. */
export const SLA_OPEN_STATES = [
  'ON_TRACK',
  'AT_RISK',
] as const satisfies readonly SlaState[];

// ---------------------------------------------------------------------------
// Notifications
// ---------------------------------------------------------------------------

/** The ten events required by Spec §13, plus SLA warning/breach split. */
export const NOTIFICATION_TYPES = [
  'CONFIRMATION_NEW',
  'CONFIRMATION_RECEIVED',
  'CONFIRMATION_READ',
  'REPLY_RECEIVED',
  'ATTACHMENT_ADDED',
  'ASSIGNMENT',
  'SLA_APPROACHING',
  'SLA_OVERDUE',
  'CONFIRMATION_RESOLVED',
  'CONFIRMATION_CLOSED',
] as const;
export const notificationTypeSchema = enumOf(NOTIFICATION_TYPES);
export type NotificationType = z.infer<typeof notificationTypeSchema>;

/**
 * NOTIF-004: channels are data, so adding EMAIL or SMS later touches the
 * delivery module only — never the confirmation domain.
 */
export const NOTIFICATION_CHANNELS = ['IN_APP', 'EMAIL', 'SMS', 'PUSH'] as const;
export const notificationChannelSchema = enumOf(NOTIFICATION_CHANNELS);
export type NotificationChannel = z.infer<typeof notificationChannelSchema>;

export const DELIVERY_STATUSES = ['PENDING', 'SENT', 'FAILED', 'SKIPPED'] as const;
export const deliveryStatusSchema = enumOf(DELIVERY_STATUSES);
export type DeliveryStatus = z.infer<typeof deliveryStatusSchema>;

// ---------------------------------------------------------------------------
// Audit
// ---------------------------------------------------------------------------

/**
 * Audit actions are a closed vocabulary so the audit log stays queryable and
 * the timeline renderer is exhaustive. Adding an action is a deliberate act.
 * AUD-002.
 */
export const AUDIT_ACTIONS = [
  // authentication
  'auth.login_succeeded',
  'auth.login_failed',
  'auth.logout',
  'auth.password_changed',
  'auth.password_reset_requested',
  'auth.password_reset_completed',
  'auth.account_locked',
  'auth.token_reuse_detected',
  // offices
  'office.created',
  'office.updated',
  'office.disabled',
  'office.archived',
  // users
  'user.created',
  'user.updated',
  'user.disabled',
  'user.archived',
  'user.password_reset_by_admin',
  'user.role_changed',
  'user.office_changed',
  // confirmations
  'confirmation.drafted',
  'confirmation.sent',
  'confirmation.received',
  'confirmation.read',
  'confirmation.replied',
  'confirmation.resolved',
  'confirmation.closed',
  'confirmation.reopened',
  'confirmation.cancelled',
  'confirmation.reassigned',
  'confirmation.viewed_by_admin',
  // messages & attachments
  'message.posted',
  'attachment.uploaded',
  'attachment.downloaded',
  'attachment.scan_completed',
  'attachment.deleted',
  // sla & settings
  'sla.rule_created',
  'sla.rule_updated',
  'sla.breached',
  'settings.updated',
] as const;
export const auditActionSchema = enumOf(AUDIT_ACTIONS);
export type AuditAction = z.infer<typeof auditActionSchema>;
